import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from "@angular/core"
import { CommonModule } from "@angular/common"
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms"

import { MatCardModule } from "@angular/material/card"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatInputModule } from "@angular/material/input"
import { MatButtonModule } from "@angular/material/button"
import { MatIconModule } from "@angular/material/icon"
import { MatSlideToggleModule } from "@angular/material/slide-toggle"
import { MatSnackBarModule } from "@angular/material/snack-bar"
import { MatSelectModule } from "@angular/material/select"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"

import { Router, RouterModule } from "@angular/router"
import { Subscription, concat, of } from "rxjs"
import { last, switchMap, tap } from "rxjs/operators"

import { SnackbarService } from "../../services/snackbar.service"
import { AuthService } from "../../services/auth.service"
import { ConfirmDialogService } from "../../services/confirm-dialog.service"
import { ProfileService, textNotificationsEnabled } from "../../services/profile.service"
import { PermissionService } from "../../services/permission.service"
import { StationContextService } from "../../services/station-context.service"
import { StationStoreService } from "../../services/station-store.service"
import { OrderRuleService } from "../../services/order-rule.service"
import {
  NotificationPreferencesService,
  NotificationPreference,
  ADMIN_NOTIFICATION_PREF_DEFAULTS,
  isAdminOnlyNotificationEvent,
  isWeeklyDigestEventType,
} from "../../services/notification-preferences.service"
import { canManageStationConfig, normalizeStationRole } from "../../utils/station-role.util"
import { collapseRepeatedSpaces } from "../../utils/text"
import {
  SignupPurposeCategoriesService,
  SignupPurposeCategory,
} from "../../services/signup-purpose-categories.service"

import { User } from "../../models/user.model"
import { Station } from "../../models/station.model"
import { LeadTimeType } from "../../models/order-rule.model"

@Component({
  selector: "app-user-settings",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    RouterModule,
  ],
  templateUrl: "./user-settings.component.html",
  styleUrl: "./user-settings.component.css",
})
export class UserSettingsComponent implements OnInit, OnDestroy {
  @ViewChild("phoneOtpInput") phoneOtpInput?: ElementRef<HTMLInputElement>
  @ViewChild("phoneCollectInput") phoneCollectInput?: ElementRef<HTMLInputElement>

  settingsForm: FormGroup
  orderRuleForm: FormGroup
  phoneCollectForm: FormGroup

  loading = false
  saving = false

  loadingOrderRule = false
  savingOrderRule = false
  stationsForRules: Station[] = []
  loadingStationsForRules = false
  lockedStationId: number | null = null
  /** Station whose rule is currently in orderRuleForm (null while loading / after a failed load). */
  private orderRuleLoadedStationId: number | null = null

  authoritativeMinimum: string | null = null

  user: any = null

  showPhoneOtpModal = false
  showPhoneCollectModal = false
  phoneCollectError = ""
  savingPhone = false
  phoneOtpCode = ""
  phoneOtpError = ""
  phoneOtpResendCountdown = 0
  isVerifyingOtp = false

  private savedTextNotifications = false
  private suppressToggleHandler = false
  private resendCountdownTimer: ReturnType<typeof setInterval> | null = null
  private subs = new Subscription()
  private notifPrefService = inject(NotificationPreferencesService)

  notificationPreferences: NotificationPreference[] = []
  private savedNotificationPreferences: NotificationPreference[] = []
  loadingPreferences = false
  savingPageChanges = false

  signupPurposeCategories: SignupPurposeCategory[] = []
  private savedCategoryActive: Record<number, boolean> = {}
  loadingSignupCategories = false
  savingSignupCategory = false
  newCategoryLabel = ""
  editingCategoryId: number | null = null
  editingCategoryLabel = ""
  private purposeCategoriesService = inject(SignupPurposeCategoriesService)

  /**
   * Sanity cap for the input only: put_station_order_rule accepts any integer >= 0, so keep this
   * generous (one year) so existing stored rules stay editable.
   */
  static readonly MAX_LEAD_TIME_VALUE = 365
  readonly maxLeadTimeValue = UserSettingsComponent.MAX_LEAD_TIME_VALUE

  readonly leadTimeTypes: { value: LeadTimeType; label: string }[] = [
    { value: "CALENDAR_DAYS", label: "Calendar days" },
    { value: "WORKING_DAYS", label: "Working days (Mon–Fri)" },
  ]

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private profileService: ProfileService,
    private snackBar: SnackbarService,
    private permissionService: PermissionService,
    private stationContext: StationContextService,
    private stationStore: StationStoreService,
    private orderRuleService: OrderRuleService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
  ) {
    this.settingsForm = this.formBuilder.group({
      textnotifications: [false, Validators.required],
    })

    this.orderRuleForm = this.formBuilder.group({
      stationId: [null as number | null],
      leadTimeType: ["WORKING_DAYS" as LeadTimeType, Validators.required],
      leadTimeValue: [
        2,
        [
          Validators.required,
          Validators.min(0),
          Validators.max(UserSettingsComponent.MAX_LEAD_TIME_VALUE),
          Validators.pattern(/^\d+$/),
        ],
      ],
    })

    this.phoneCollectForm = this.formBuilder.group({
      phonenumber: ["", [Validators.required, Validators.pattern(/^\+?[\d\s-]+$/)]],
    })
  }

  ngOnInit(): void {
    this.loadUserSettings()
    if (this.canAccessOrderRulesSection) {
      this.loadStationsForOrderRule()
    }
    if (this.isStaffRole) {
      this.loadNotificationPreferences()
    }
    if (this.isAdminRole) {
      this.loadSignupPurposeCategories()
    }

    this.subs.add(
      this.permissionService.currentRole$.subscribe(() => {
        /* refresh getters bound in template */
      }),
    )

    this.subs.add(
      this.orderRuleForm.valueChanges.subscribe(() => {
        this.authoritativeMinimum = null
      }),
    )

    this.subs.add(
      this.settingsForm.get("textnotifications")!.valueChanges.subscribe((enabled: boolean) => {
        if (this.suppressToggleHandler) return
        this.handleTextNotificationsToggle(enabled)
      }),
    )
  }

  ngOnDestroy(): void {
    this.clearResendCountdown()
    this.subs.unsubscribe()
  }

  get canAccessOrderRulesSection(): boolean {
    if (this.permissionService.getBaseRole() === "admin") return true
    return this.stationContext
      .getAssignments()
      .some((a) => canManageStationConfig(a.station_role))
  }

  get canManageOrderRules(): boolean {
    const sid = this.orderRuleForm.get("stationId")?.value as number | null
    const station = this.stationsForRules.find((s) => s.id === sid)
    const role = normalizeStationRole(
      station?.user_role ??
        (sid != null ? this.stationContext.getRoleForStation(sid) : null) ??
        this.permissionService.getCurrentRole(),
    )
    return canManageStationConfig(role)
  }

  get canViewTextNotifications(): boolean {
    return this.permissionService.getBaseRole() === "user"
  }

  /**
   * "Am I station staff anywhere?" — decides whether this page shows staff
   * notification preferences or plain user settings.
   *
   * Derived from the assignment list rather than the global users.role column:
   * that column records the role of the first station a user was ever added to and
   * is never refreshed, so an advertiser later given a station assignment kept
   * being shown the advertiser-only page. This is intentionally station-agnostic —
   * the question is about the account, not the selected station.
   */
  get isStaffRole(): boolean {
    return !this.permissionService.isAdvertiserUserOnly()
  }

  get isAdminRole(): boolean {
    return this.permissionService.getBaseRole() === "admin"
  }

  get hasUnsavedChanges(): boolean {
    return this.prefsDirty || this.categoriesDirty
  }

  private get prefsDirty(): boolean {
    if (this.notificationPreferences.length !== this.savedNotificationPreferences.length) {
      return true
    }
    return this.notificationPreferences.some((pref) => {
      const saved = this.savedNotificationPreferences.find((row) => row.event_type === pref.event_type)
      if (!saved) return true
      return saved.inapp_enabled !== pref.inapp_enabled || saved.email_enabled !== pref.email_enabled
    })
  }

  private get categoriesDirty(): boolean {
    return this.signupPurposeCategories.some(
      (category) => !!category.is_active !== !!this.savedCategoryActive[category.id],
    )
  }

  /** Staff see existing rows; admin-only signup/digest rows are appended for admin. */
  get visibleNotificationPreferences(): NotificationPreference[] {
    if (this.isAdminRole) return this.notificationPreferences
    return this.notificationPreferences.filter((pref) => !isAdminOnlyNotificationEvent(pref.event_type))
  }

  /** Stations whose order rule this user may edit (admin: all; otherwise owner/station_owner). */
  get manageableStationsForRules(): Station[] {
    if (this.permissionService.getBaseRole() === "admin") return this.stationsForRules
    return this.stationsForRules.filter((s) =>
      canManageStationConfig(s.user_role ?? this.stationContext.getRoleForStation(s.id)),
    )
  }

  /** Anyone managing more than one station must be able to pick which rule to edit. */
  get showStationDropdown(): boolean {
    return this.manageableStationsForRules.length > 1
  }

  get canResendPhoneOtp(): boolean {
    return this.phoneOtpResendCountdown <= 0
  }

  get maskedPhone(): string {
    const phone = this.user?.phonenumber
    if (!phone || typeof phone !== "string") return "***"
    const digits = phone.replace(/\D/g, "")
    if (digits.length < 3) return "***"
    return "***" + digits.slice(-3)
  }

  get orderRulePreviewLabel(): string {
    const t = this.orderRuleForm.get("leadTimeType")?.value as LeadTimeType
    const v = this.orderRuleForm.get("leadTimeValue")?.value
    const d = this.orderRuleService.previewMinimumStartDateLocal(t, v)
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
  }

  /** Save only once the rule for the selected station is in the form (never another station's values). */
  get canSaveOrderRule(): boolean {
    const sid = this.effectiveStationIdForRule
    return (
      sid != null &&
      this.canManageOrderRules &&
      !this.loadingOrderRule &&
      !this.savingOrderRule &&
      this.orderRuleLoadedStationId === sid &&
      this.orderRuleForm.valid
    )
  }

  get orderRuleStationLabel(): string {
    const s = this.manageableStationsForRules.find((x) => x.id === this.effectiveStationIdForRule)
    return s ? `${s.name} (${s.call_sign})` : "—"
  }

  get leadTimeUnitLabel(): string {
    return this.orderRuleForm.get("leadTimeType")?.value === "CALENDAR_DAYS" ? "calendar days" : "working days"
  }

  get effectiveStationIdForRule(): number | null {
    return this.orderRuleForm.get("stationId")?.value ?? this.lockedStationId
  }

  private loadUserSettings(): void {
    this.loading = true

    const email =
      this.profileService.getCurrentUser()?.email ??
      this.authService.getCurrentUserInfo()?.email

    if (!email) {
      this.authService.getProfile().subscribe({
        next: (data) => {
          this.user = data
          this.applyTextNotificationsToForm(data.textnotifications)
          this.loading = false
        },
        error: (error) => {
          console.error("Error loading user settings:", error)
          this.snackBar.error("Error loading user settings", 5000)
          this.loading = false
        },
      })
      return
    }

    this.profileService.getProfile(email).subscribe({
      next: (data) => {
        this.user = data
        this.applyTextNotificationsToForm(data.textnotifications)
        this.loading = false
      },
      error: (error) => {
        console.error("Error loading user settings:", error)
        const fallback = this.authService.getCurrentUserInfo()
        if (fallback) {
          this.user = fallback
          this.applyTextNotificationsToForm(fallback.textnotifications)
        } else {
          this.snackBar.error("Error loading user settings", 5000)
        }
        this.loading = false
      },
    })
  }

  private hasPhoneNumber(): boolean {
    const phone = this.user?.phonenumber
    return typeof phone === "string" && phone.trim().length > 0
  }

  private handleTextNotificationsToggle(enabled: boolean): void {
    if (enabled) {
      if (!this.hasPhoneNumber()) {
        this.openPhoneCollectModal()
        return
      }
      this.saveTextNotifications(true)
      return
    }

    this.saveTextNotifications(false)
  }

  private openPhoneCollectModal(): void {
    this.showPhoneCollectModal = true
    this.phoneCollectError = ""
    this.phoneCollectForm.reset()
    setTimeout(() => this.phoneCollectInput?.nativeElement.focus(), 0)
  }

  cancelPhoneCollectModal(): void {
    this.showPhoneCollectModal = false
    this.phoneCollectError = ""
    this.phoneCollectForm.reset()
    this.revertToggle(false)
  }

  submitPhoneCollect(): void {
    if (this.phoneCollectForm.invalid) {
      this.markFormGroupTouched(this.phoneCollectForm)
      return
    }

    const rawPhone = this.phoneCollectForm.get("phonenumber")?.value ?? ""
    const phonenumber = this.normalizePhone(rawPhone)
    this.savingPhone = true
    this.phoneCollectError = ""

    this.authService.updateUser({ phonenumber, textnotifications: true }).subscribe({
      next: (response) => {
        this.savingPhone = false
        this.showPhoneCollectModal = false
        this.phoneCollectForm.reset()
        this.user = { ...this.user, phonenumber, ...(response?.user ?? {}) }

        if (response?.status === "phone_verification_required") {
          this.openPhoneOtpModal()
          return
        }

        this.applySuccessfulSave(true, response)
        this.snackBar.success("Settings saved", 3000)
      },
      error: (error) => {
        this.savingPhone = false
        console.error("Error saving phone number:", error)

        if (error.status === 409) {
          this.phoneCollectError = "This phone number is already in use by another account."
          return
        }

        this.phoneCollectError = error.error?.message || "Failed to save phone number"
      },
    })
  }

  private normalizePhone(phone: string): string {
    const cleaned = phone.replace(/[\s\-\(\)]/g, "")
    if (cleaned.startsWith("+")) return cleaned
    if (cleaned.length === 10) return "+1" + cleaned
    return cleaned
  }

  private saveTextNotifications(enabled: boolean): void {
    this.saving = true

    this.authService.updateUser({ textnotifications: enabled }).subscribe({
      next: (response) => {
        this.saving = false

        if (enabled && response?.status === "phone_verification_required") {
          this.openPhoneOtpModal()
          return
        }

        this.applySuccessfulSave(enabled, response)
        this.snackBar.success("Settings saved", 3000)
      },
      error: (error) => {
        this.saving = false
        console.error("Error updating settings:", error)
        this.revertToggle(this.savedTextNotifications)

        if (error.status === 409) {
          this.snackBar.error("This phone number is already in use by another account.", 5000)
          return
        }

        this.snackBar.error(error.error?.message || "Error updating settings", 5000)
      },
    })
  }

  private openPhoneOtpModal(): void {
    this.showPhoneOtpModal = true
    this.phoneOtpCode = ""
    this.phoneOtpError = ""
    this.isVerifyingOtp = false
    this.startPhoneOtpResendCountdown()

    setTimeout(() => this.phoneOtpInput?.nativeElement.focus(), 0)
  }

  private closePhoneOtpModal(): void {
    this.showPhoneOtpModal = false
    this.phoneOtpCode = ""
    this.phoneOtpError = ""
    this.isVerifyingOtp = false
    this.clearResendCountdown()
  }

  onPhoneOtpInput(event: Event): void {
    const input = event.target as HTMLInputElement
    const value = input.value.replace(/\D/g, "").slice(0, 6)
    input.value = value
    this.phoneOtpCode = value
  }

  verifyPhoneOtpInModal(): void {
    const code = this.phoneOtpCode.trim()
    if (code.length !== 6) {
      this.phoneOtpError = "Please enter a 6-digit code"
      return
    }

    const email = this.user?.email ?? this.authService.getCurrentUserInfo()?.email
    if (!email) {
      this.phoneOtpError = "Unable to verify — email not found"
      return
    }

    this.isVerifyingOtp = true
    this.phoneOtpError = ""

    this.authService.verifyPhoneOtp(email, code, "settings").subscribe({
      next: () => {
        this.isVerifyingOtp = false
        this.closePhoneOtpModal()
        this.applySuccessfulSave(true, { user: { textnotifications: 1, phone_verified: true } })
        this.snackBar.success("Phone verified! Text notifications enabled.", 3000)
      },
      error: () => {
        this.isVerifyingOtp = false
        this.phoneOtpError = "Invalid or expired code"
        this.phoneOtpCode = ""
        setTimeout(() => this.phoneOtpInput?.nativeElement.focus(), 0)
      },
    })
  }

  resendPhoneOtpInModal(): void {
    if (!this.canResendPhoneOtp) return

    const email = this.user?.email ?? this.authService.getCurrentUserInfo()?.email
    if (!email) return

    this.phoneOtpError = ""

    this.authService.resendPhoneOtp(email).subscribe({
      next: () => {
        this.snackBar.success("Code resent! Check your mobile.", 3000)
        this.startPhoneOtpResendCountdown()
      },
      error: (error) => {
        this.phoneOtpError = error.error?.message || "Failed to resend code. Please try again."
      },
    })
  }

  private startPhoneOtpResendCountdown(): void {
    this.clearResendCountdown()
    this.phoneOtpResendCountdown = 60

    this.resendCountdownTimer = setInterval(() => {
      this.phoneOtpResendCountdown--
      if (this.phoneOtpResendCountdown <= 0) {
        this.clearResendCountdown()
      }
    }, 1000)
  }

  private clearResendCountdown(): void {
    if (this.resendCountdownTimer) {
      clearInterval(this.resendCountdownTimer)
      this.resendCountdownTimer = null
    }
    if (this.phoneOtpResendCountdown > 0) {
      this.phoneOtpResendCountdown = 0
    }
  }

  private applySuccessfulSave(enabled: boolean, response: any): void {
    const mergedUser = {
      ...this.user,
      ...(response?.user ?? {}),
      textnotifications: enabled ? 1 : 0,
    }

    this.user = mergedUser
    this.savedTextNotifications = enabled
    this.suppressToggleHandler = true
    this.settingsForm.patchValue({ textnotifications: enabled }, { emitEvent: false })
    this.suppressToggleHandler = false
    this.settingsForm.markAsPristine()

    const email = mergedUser?.email ?? this.authService.getCurrentUserInfo()?.email
    if (email) {
      this.profileService.getProfile(email).subscribe({ error: () => {} })
    }
  }

  private revertToggle(value: boolean): void {
    this.suppressToggleHandler = true
    this.settingsForm.patchValue({ textnotifications: value }, { emitEvent: false })
    this.suppressToggleHandler = false
  }

  private loadStationsForOrderRule(): void {
    this.loadingStationsForRules = true
    this.subs.add(
      this.stationStore.watchStations().subscribe({
        next: (stations) => {
          this.stationsForRules = stations
          this.loadingStationsForRules = false

          const manageable = this.manageableStationsForRules
          this.lockedStationId = manageable.length === 1 ? manageable[0].id : null
          // watchStations() re-emits on background refresh: keep the station being edited
          // unless it disappeared, and default to the first station the user can manage.
          const current = this.orderRuleForm.get("stationId")?.value as number | null
          if (current != null && manageable.some((s) => s.id === current)) return
          const sid = manageable[0]?.id ?? null
          this.orderRuleForm.patchValue({ stationId: sid }, { emitEvent: false })
          if (sid != null) {
            this.fetchOrderRuleForStation(sid)
          }
        },
        error: () => {
          this.loadingStationsForRules = false
          this.snackBar.error("Failed to load stations for order rules", 4000)
        },
      }),
    )
  }

  onOrderRuleStationSelectionChange(): void {
    const sid = this.orderRuleForm.get("stationId")?.value
    if (sid != null) {
      this.fetchOrderRuleForStation(sid)
    }
  }

  private fetchOrderRuleForStation(stationId: number): void {
    this.loadingOrderRule = true
    this.orderRuleLoadedStationId = null
    this.authoritativeMinimum = null
    this.subs.add(
      this.orderRuleService.getStationOrderRule(stationId).subscribe({
        next: (rule) => {
          // A slower response for a station the user already switched away from must not
          // overwrite the form (saveOrderRule would then write A's values to B).
          if (this.effectiveStationIdForRule !== stationId) return
          this.orderRuleForm.patchValue(
            {
              leadTimeType: rule.leadTimeType,
              leadTimeValue: rule.leadTimeValue,
            },
            { emitEvent: false },
          )
          this.authoritativeMinimum = rule.minimumStartDate
          this.orderRuleLoadedStationId = stationId
          this.loadingOrderRule = false
        },
        error: () => {
          if (this.effectiveStationIdForRule !== stationId) return
          this.loadingOrderRule = false
          this.snackBar.error("Failed to load order start date rule", 4000)
        },
      }),
    )
  }

  onSubmit(): void {
    if (this.settingsForm.invalid) {
      this.markFormGroupTouched(this.settingsForm)
      return
    }

    const formData = this.settingsForm.value
    if (formData.textnotifications === this.savedTextNotifications) {
      return
    }

    this.handleTextNotificationsToggle(formData.textnotifications)
  }

  saveOrderRule(): void {
    if (!this.canManageOrderRules || this.savingOrderRule) return

    const sid = this.effectiveStationIdForRule
    if (sid == null) {
      this.snackBar.error("Select a station", 3000)
      return
    }

    // The form must hold this station's rule — never save values loaded for another station.
    if (this.loadingOrderRule || this.orderRuleLoadedStationId !== sid) {
      this.snackBar.error("The order rule for this station has not loaded yet", 3000)
      return
    }

    if (this.orderRuleForm.invalid) {
      this.markFormGroupTouched(this.orderRuleForm)
      return
    }

    const { leadTimeType, leadTimeValue } = this.orderRuleForm.getRawValue()
    this.savingOrderRule = true
    this.orderRuleService
      .updateStationOrderRule(sid, { leadTimeType, leadTimeValue: Number(leadTimeValue) })
      .subscribe({
        next: (rule) => {
          this.savingOrderRule = false
          if (this.effectiveStationIdForRule !== sid) return
          this.authoritativeMinimum = rule.minimumStartDate
          this.snackBar.success("Order start date rule saved", 3000)
        },
        error: (err) => {
          this.savingOrderRule = false
          const msg = err.error?.error || err.error?.message || "Failed to save rule"
          this.snackBar.error(msg, 5000)
        },
      })
  }

  private markFormGroupTouched(form: FormGroup): void {
    Object.keys(form.controls).forEach((key) => {
      form.get(key)?.markAsTouched()
    })
  }

  onCancel(): void {
    if (this.saving) return

    this.revertToggle(this.savedTextNotifications)
    this.settingsForm.markAsPristine()
    void this.router.navigate(["/profile"])
  }

  private applyTextNotificationsToForm(value: unknown): void {
    this.savedTextNotifications = textNotificationsEnabled(value)
    this.suppressToggleHandler = true
    this.settingsForm.patchValue({ textnotifications: this.savedTextNotifications }, { emitEvent: false })
    this.suppressToggleHandler = false
    this.settingsForm.markAsPristine()
  }

  authoritativeMinimumDisplay(): string {
    if (!this.authoritativeMinimum) return ""
    try {
      return this.orderRuleService.parseIsoDateLocal(this.authoritativeMinimum).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    } catch {
      return this.authoritativeMinimum
    }
  }

  get phoneCollectControl() {
    return this.phoneCollectForm.get("phonenumber")
  }

  loadNotificationPreferences(): void {
    this.loadingPreferences = true
    this.notifPrefService.getPreferences().subscribe({
      next: (res) => {
        const normalized = this.normalizeNotificationPreferences(res.preferences || [])
        this.notificationPreferences = this.clonePrefs(normalized)
        this.savedNotificationPreferences = this.clonePrefs(normalized)
        this.loadingPreferences = false
      },
      error: (err) => {
        console.error("Failed to load notif prefs", err)
        this.loadingPreferences = false
      },
    })
  }

  toggleInApp(pref: NotificationPreference): void {
    pref.inapp_enabled = pref.inapp_enabled === 1 ? 0 : 1
  }

  toggleEmail(pref: NotificationPreference): void {
    pref.email_enabled = pref.email_enabled === 1 ? 0 : 1
  }

  onPageSave(): void {
    if (!this.hasUnsavedChanges || this.savingPageChanges) return
    this.savingPageChanges = true

    const prefs$ = this.prefsDirty
      ? this.notifPrefService.updatePreferences(this.notificationPreferences).pipe(
          tap(() => {
            this.savedNotificationPreferences = this.clonePrefs(this.notificationPreferences)
          }),
        )
      : of(null)

    prefs$
      .pipe(
        switchMap(() => {
          const changed = this.changedCategoryActiveStates()
          if (!changed.length) return of(null)
          return concat(
            ...changed.map((category) =>
              this.purposeCategoriesService
                .updateCategory(category.id, { is_active: !!category.is_active })
                .pipe(
                  tap(() => {
                    this.savedCategoryActive[category.id] = !!category.is_active
                  }),
                ),
            ),
          ).pipe(last())
        }),
      )
      .subscribe({
        next: () => {
          this.savingPageChanges = false
          this.snackBar.success("Settings saved.")
          void this.router.navigate(["/profile"])
        },
        error: (err) => {
          this.savingPageChanges = false
          this.snackBar.error(
            err?.error?.message || err?.error?.error || "Failed to save settings. Please try again.",
          )
        },
      })
  }

  onPageCancel(): void {
    if (this.savingPageChanges) return
    this.revertStagedToggles()
    void this.router.navigate(["/profile"])
  }

  private revertStagedToggles(): void {
    this.notificationPreferences = this.clonePrefs(this.savedNotificationPreferences)
    this.signupPurposeCategories = this.signupPurposeCategories.map((category) => ({
      ...category,
      is_active: this.savedCategoryActive[category.id] ?? category.is_active,
    }))
  }

  private changedCategoryActiveStates(): SignupPurposeCategory[] {
    return this.signupPurposeCategories.filter(
      (category) => !!category.is_active !== !!this.savedCategoryActive[category.id],
    )
  }

  private clonePrefs(prefs: NotificationPreference[]): NotificationPreference[] {
    return prefs.map((pref) => ({ ...pref }))
  }

  trackByEventType(index: number, pref: NotificationPreference): string {
    return pref.event_type
  }

  loadSignupPurposeCategories(): void {
    this.loadingSignupCategories = true
    this.purposeCategoriesService.getAdminCategories().subscribe({
      next: (categories) => {
        this.applyLoadedCategories(categories)
        this.loadingSignupCategories = false
      },
      error: () => {
        this.loadingSignupCategories = false
        this.snackBar.error("Failed to load signup purpose categories.")
      },
    })
  }

  addSignupPurposeCategory(): void {
    const label = collapseRepeatedSpaces(this.newCategoryLabel)
    if (!label || this.savingSignupCategory) return
    this.savingSignupCategory = true
    this.purposeCategoriesService.createCategory(label).subscribe({
      next: () => {
        this.newCategoryLabel = ""
        this.savingSignupCategory = false
        this.snackBar.success("Category added.")
        this.loadSignupPurposeCategories()
      },
      error: (err) => {
        this.savingSignupCategory = false
        this.snackBar.error(err.error?.message || err.error?.error || "Failed to add category.")
      },
    })
  }

  startEditSignupCategory(category: SignupPurposeCategory): void {
    this.editingCategoryId = category.id
    this.editingCategoryLabel = category.label
  }

  cancelEditSignupCategory(): void {
    this.editingCategoryId = null
    this.editingCategoryLabel = ""
  }

  saveEditSignupCategory(category: SignupPurposeCategory): void {
    const label = collapseRepeatedSpaces(this.editingCategoryLabel)
    if (!label || this.savingSignupCategory) return
    this.savingSignupCategory = true
    this.purposeCategoriesService.updateCategory(category.id, { label }).subscribe({
      next: () => {
        category.label = label
        this.savingSignupCategory = false
        this.cancelEditSignupCategory()
        this.snackBar.success("Category updated.")
      },
      error: (err) => {
        this.savingSignupCategory = false
        this.snackBar.error(err.error?.message || err.error?.error || "Failed to update category.")
      },
    })
  }

  toggleSignupCategoryActive(category: SignupPurposeCategory): void {
    category.is_active = !category.is_active
  }

  deleteSignupPurposeCategory(category: SignupPurposeCategory): void {
    if (this.savingSignupCategory || this.savingPageChanges) return
    this.confirmDialog
      .open({
        title: "Delete category",
        message: "Are you sure you want to delete this category? This cannot be undone.",
        confirmLabel: "Delete",
        cancelLabel: "Cancel",
        confirmIcon: "delete",
      })
      .subscribe((confirmed) => {
        if (!confirmed) return
        this.savingSignupCategory = true
        this.purposeCategoriesService.deleteCategory(category.id).subscribe({
          next: () => {
            this.signupPurposeCategories = this.signupPurposeCategories.filter(
              (row) => row.id !== category.id,
            )
            delete this.savedCategoryActive[category.id]
            this.savingSignupCategory = false
            if (this.editingCategoryId === category.id) {
              this.cancelEditSignupCategory()
            }
            this.snackBar.success("Category deleted.")
          },
          error: (err) => {
            this.savingSignupCategory = false
            const backendMessage = err?.error?.message || err?.error?.error
            if (err?.status === 409) {
              this.snackBar.error(backendMessage || "This category is in use and cannot be deleted.")
              return
            }
            this.snackBar.error(backendMessage || "Failed to delete category.")
          },
        })
      })
  }

  trackByCategoryId(_index: number, category: SignupPurposeCategory): number {
    return category.id
  }

  private applyLoadedCategories(categories: SignupPurposeCategory[]): void {
    const pendingActive = new Map(
      this.signupPurposeCategories.map((row) => [row.id, row.is_active] as const),
    )
    this.signupPurposeCategories = categories.map((category) => {
      const pending = pendingActive.get(category.id)
      const persisted = !!category.is_active
      if (this.savedCategoryActive[category.id] === undefined) {
        this.savedCategoryActive[category.id] = persisted
      }
      return {
        ...category,
        is_active: pending !== undefined ? pending : persisted,
      }
    })
    const remainingIds = new Set(categories.map((category) => category.id))
    for (const id of Object.keys(this.savedCategoryActive)) {
      const numericId = Number(id)
      if (!remainingIds.has(numericId)) {
        delete this.savedCategoryActive[numericId]
      }
    }
  }

  private normalizeNotificationPreferences(prefs: NotificationPreference[]): NotificationPreference[] {
    const normalized: NotificationPreference[] = prefs.map((pref) => ({
      ...pref,
      supports_inapp: isWeeklyDigestEventType(pref.event_type) ? false : pref.supports_inapp,
    }))

    if (!this.isAdminRole) return normalized

    const existingTypes = new Set(normalized.map((p) => p.event_type))
    for (const fallback of ADMIN_NOTIFICATION_PREF_DEFAULTS) {
      const aliases =
        fallback.event_type === "weekly_new_user_digest"
          ? ["weekly_new_user_digest", "weekly_user_digest", "weekly_new_users"]
          : ["new_user_signup", "user_signup", "new_signup"]
      if (aliases.some((key) => existingTypes.has(key))) continue
      normalized.push({ ...fallback })
    }
    return normalized
  }
}
